/**
 * SettingsPage — real content, four tabs.
 *
 *   Account     username, email, role, change-password link, logout
 *   Security    TOTP enroll status + enroll/disable, recent auth_audit
 *   Preferences shared-KB toggle, dark-mode toggle
 *   Danger zone "Sign out of all devices" (stubbed if /auth/logout-all 404s)
 *
 * Endpoint notes:
 *   POST /auth/enroll-totp        live in src/auth/totp-routes.js
 *   POST /auth/totp/disable       not yet implemented — UI surfaces info
 *   GET/POST /users/me/preferences/shared-kb  live (consolidated 03813f0)
 *   POST /auth/logout-all         not yet implemented — surface friendly stub
 */

import { useEffect, useState } from 'react'
import {
  Shield,
  Sun,
  Moon,
  LogOut,
  AlertTriangle,
  KeyRound,
  Brain,
  Trash2,
  Download,
} from 'lucide-react'
import { PageLayout } from '../components/PageLayout'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'

type Tab = 'account' | 'security' | 'preferences' | 'memory' | 'danger'

const TAB_LABELS: Record<Tab, string> = {
  account: 'Account',
  security: 'Security',
  preferences: 'Preferences',
  memory: 'Memory',
  danger: 'Danger zone',
}

type MemoryRow = {
  id: string
  kind: string
  body: string
  why: string | null
  use_count: number
  last_used_at: string | null
  created_at: string
  expires_at: string | null
}

type MemoryByKind = Record<string, MemoryRow[]>

const KIND_LABELS: Record<string, string> = {
  vocabulary: 'Vocabulary',
  correction: 'Corrections',
  preference: 'Preferences',
  recurring_question: 'Recurring questions',
  fact: 'Facts',
  pattern: 'Patterns',
}

const KIND_ORDER = [
  'vocabulary',
  'correction',
  'preference',
  'pattern',
  'fact',
  'recurring_question',
]

function AccountTab() {
  const { claims, logout } = useAuth()
  if (!claims) return null
  return (
    <div className="space-y-4">
      <Field label="Username" value={claims.username} />
      <Field label="Role" value={claims.role || 'owner'} />
      <Field label="Client" value={claims.client_id || '—'} />
      <Field label="User ID" value={claims.user_id} />
      <div className="pt-2">
        <button
          type="button"
          onClick={() => logout()}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-slate-800 dark:text-slate-100">{value}</div>
    </div>
  )
}

type Session = {
  jti: string
  device: string
  ip: string
  created_at: string
  is_current: boolean
}

function ActiveSessionsSection() {
  const [sessions, setSessions] = useState<Session[] | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)

  const loadSessions = () => {
    fetch('/auth/sessions', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : { sessions: [] }))
      .then((data: { sessions?: Session[] }) => {
        setSessions(Array.isArray(data?.sessions) ? data.sessions : [])
      })
      .catch(() => setSessions([]))
  }

  useEffect(() => { loadSessions() }, [])

  const revokeSession = async (jti: string) => {
    setRevoking(jti)
    try {
      const res = await fetch(`/auth/sessions/${encodeURIComponent(jti)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (res.ok) {
        setSessions((prev) => prev ? prev.filter((s) => s.jti !== jti) : prev)
      }
    } catch { /* best effort */ }
    setRevoking(null)
  }

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    } catch { return iso }
  }

  if (sessions === null) {
    return <p className="mt-2 text-xs text-slate-400">Loading sessions…</p>
  }

  if (sessions.length === 0) {
    return <p className="mt-2 text-xs text-slate-400">No active sessions found.</p>
  }

  return (
    <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
      {sessions.map((s) => (
        <li
          key={s.jti}
          className="flex items-center justify-between px-3 py-2 text-xs"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-slate-700 dark:text-slate-200 truncate">
              {s.device}
            </span>
            {s.is_current && (
              <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                This device
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-slate-400">{s.ip}</span>
            <span className="text-slate-400">{formatDate(s.created_at)}</span>
            {!s.is_current && (
              <button
                type="button"
                onClick={() => revokeSession(s.jti)}
                disabled={revoking === s.jti}
                className="text-rose-500 hover:text-rose-700 dark:hover:text-rose-400 font-medium disabled:opacity-50"
              >
                {revoking === s.jti ? '…' : 'Revoke'}
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

function SecurityTab() {
  const [audit, setAudit] = useState<Array<{ created_at: string; event_type: string; ip: string | null }> | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/auth/audit?limit=10', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data: { items?: Array<{ created_at: string; event_type: string; ip: string | null }> }) => {
        if (!cancelled) setAudit(Array.isArray(data?.items) ? data.items : [])
      })
      .catch(() => {
        if (!cancelled) setAudit([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Fork 11 (2026-04-17): TOTP enrollment is parked behind a disabled
  // button until the new portal ships /auth/verify-totp. Currently
  // AuthContext.login() rejects any mfa_required response because the UI
  // has no second-factor step — so enrolling TOTP here would immediately
  // lock this user out of the new portal. Operators who need MFA should
  // use the legacy portal for the time being. Re-enable this flow in the
  // dedicated MFA-UI fork.
  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Two-factor authentication
          </h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Multi-factor login is coming soon. Until then,
          please use the <a
            href="https://butler.magicmoneyllc.com"
            target="_blank"
            rel="noreferrer"
            className="text-butler-600 underline hover:text-butler-700"
          >legacy portal</a> to enable MFA — enrolling here today would
          lock you out of this new portal on your next sign-in.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="MFA verification UI is shipping in a follow-up fork. Use the legacy portal for MFA today."
            className="rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white opacity-40 cursor-not-allowed"
          >
            Enroll TOTP
          </button>
          <button
            type="button"
            disabled
            title="Disable endpoint coming in a follow-up fork."
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-500 disabled:opacity-50"
          >
            Disable
          </button>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Active sessions
          </h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Devices where your account is currently signed in.
        </p>
        <ActiveSessionsSection />
      </section>

      <section>
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Recent sign-in activity
          </h3>
        </div>
        {audit === null ? (
          <p className="mt-2 text-xs text-slate-400">Loading…</p>
        ) : audit.length === 0 ? (
          <p className="mt-2 text-xs text-slate-400">
            No recent activity (or audit endpoint not exposed yet).
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
            {audit.map((row, idx) => (
              <li
                key={`${row.created_at}-${idx}`}
                className="flex items-center justify-between px-3 py-1.5 text-xs"
              >
                <span className="text-slate-700 dark:text-slate-200">{row.event_type}</span>
                <span className="text-slate-400">{row.ip ?? '—'}</span>
                <time className="text-slate-400">{row.created_at}</time>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function PreferencesTab() {
  const { theme, toggleTheme } = useTheme()
  const [sharedKb, setSharedKb] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/users/me/preferences/shared-kb', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : { shared_kb_optin: false }))
      .then((data: { shared_kb_optin?: boolean; optin?: boolean }) => {
        if (!cancelled) setSharedKb(Boolean(data?.shared_kb_optin ?? data?.optin ?? false))
      })
      .catch(() => {
        if (!cancelled) setSharedKb(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const toggle = async () => {
    if (sharedKb === null) return
    const next = !sharedKb
    setSaving(true)
    setError(null)
    try {
      const res = await fetch('/users/me/preferences/shared-kb', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optin: next }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message || `HTTP ${res.status}`)
      }
      setSharedKb(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <ToggleRow
        label="Share my anonymized KB feedback"
        hint="Helps Brian improve the recipe library — no event-level data leaves Butler Amusements."
        checked={!!sharedKb}
        onToggle={toggle}
        loading={saving}
      />
      {error && <p className="text-xs text-rose-500">{error}</p>}

      <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
        <ToggleRow
          label="Dark mode"
          hint="Use the dark palette across the hub."
          icon={theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          checked={theme === 'dark'}
          onToggle={toggleTheme}
        />
      </div>
    </div>
  )
}

function ToggleRow({
  label,
  hint,
  checked,
  onToggle,
  loading,
  icon,
}: {
  label: string
  hint?: string
  checked: boolean
  onToggle: () => void
  loading?: boolean
  icon?: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-2">
        {icon && <span className="mt-0.5 text-slate-500">{icon}</span>}
        <div>
          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</div>
          {hint && (
            <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</div>
          )}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        onClick={onToggle}
        disabled={loading}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-500 ${
          checked ? 'bg-butler-600' : 'bg-slate-300 dark:bg-slate-600'
        } ${loading ? 'opacity-50' : ''}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

function MemoryTab() {
  const [byKind, setByKind] = useState<MemoryByKind | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = async () => {
    setError(null)
    try {
      const res = await fetch('/api/agent-memory', { credentials: 'same-origin' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as { by_kind?: MemoryByKind }
      setByKind(data.by_kind ?? {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed')
      setByKind({})
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const forget = async (id: string) => {
    setDeletingId(id)
    try {
      const res = await fetch(`/api/agent-memory/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const totalCount =
    byKind === null
      ? null
      : Object.values(byKind).reduce((sum, arr) => sum + arr.length, 0)

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            What the agent remembers about you
          </h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          The agent captures durable facts (your vocabulary, preferences,
          corrections) so it gets faster and more useful over time. Nothing
          about customers, bands, or transactions is ever stored here. Remove
          anything you don&apos;t want remembered — the agent will stop using it
          on the next turn.
        </p>

        <div className="mt-3 flex items-center gap-2">
          <a
            href="/api/agent-memory/export"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
          >
            <Download className="h-3.5 w-3.5" />
            Export JSON
          </a>
          <span className="text-xs text-slate-400">
            {totalCount === null
              ? 'Loading…'
              : `${totalCount} ${totalCount === 1 ? 'memory' : 'memories'}`}
          </span>
        </div>

        {error && (
          <p className="mt-2 text-xs text-rose-500" role="alert">
            {error}
          </p>
        )}

        {byKind !== null && totalCount === 0 && !error && (
          <p className="mt-4 text-xs text-slate-400">
            No memories yet. As you chat with the agent, it will start
            capturing durable facts here.
          </p>
        )}

        {byKind !== null && totalCount! > 0 && (
          <div className="mt-4 space-y-5">
            {KIND_ORDER.filter((k) => (byKind[k]?.length ?? 0) > 0).map((k) => (
              <div key={k}>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {KIND_LABELS[k] || k}
                </div>
                <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                  {byKind[k].map((row) => (
                    <li
                      key={row.id}
                      className="flex items-start justify-between gap-3 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800 dark:text-slate-100">
                          {row.body}
                        </div>
                        {row.why && (
                          <div className="mt-0.5 text-xs text-slate-400">
                            {row.why}
                          </div>
                        )}
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          used {row.use_count}×
                          {row.expires_at && ` · expires ${row.expires_at.slice(0, 10)}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => forget(row.id)}
                        disabled={deletingId === row.id}
                        aria-label={`Forget: ${row.body}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-rose-300 hover:text-rose-600 disabled:opacity-50 dark:border-slate-600"
                      >
                        <Trash2 className="h-3 w-3" />
                        Forget
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function DangerTab() {
  const [status, setStatus] = useState<string | null>(null)
  const signOutAll = async () => {
    setStatus('Signing out of all devices…')
    try {
      const res = await fetch('/auth/logout-all', {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (res.status === 404) {
        setStatus('Endpoint not yet implemented — coming in a follow-up.')
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setStatus('All sessions revoked.')
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'request failed')
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/40 dark:bg-rose-900/10">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-rose-500" aria-hidden="true" />
        <div className="text-xs text-rose-800 dark:text-rose-200">
          These actions cannot be undone. Sign-outs will close every active
          session for your account, including this browser.
        </div>
      </div>
      <button
        type="button"
        onClick={signOutAll}
        className="inline-flex items-center gap-2 rounded-md bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-600"
      >
        <LogOut className="h-4 w-4" />
        Sign out of all devices
      </button>
      {status && <p className="text-xs text-slate-500">{status}</p>}
    </div>
  )
}

const TABS: Tab[] = ['account', 'security', 'preferences', 'memory', 'danger']

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('account')

  return (
    <PageLayout title="Settings" subtitle="Account, security, and preferences">
      <div role="tablist" aria-label="Settings sections" className="mb-4 flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`rounded-t-md border-b-2 px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-500 ${
              tab === t
                ? 'border-butler-600 text-butler-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {tab === 'account' && <AccountTab />}
        {tab === 'security' && <SecurityTab />}
        {tab === 'preferences' && <PreferencesTab />}
        {tab === 'memory' && <MemoryTab />}
        {tab === 'danger' && <DangerTab />}
      </div>
    </PageLayout>
  )
}
